import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AccessRequestPendingResponse,
  AccessRequestUid,
  FindBusinessResponse,
} from '../models';
import { IBaseResponse } from '@carm/_utils/models';
import { environment } from 'src/environments/environment';
import {
  ICreateUserRequest,
  PostStaffMemberAccessRequest,
} from '@carm/features/private/setup-my-portal/employees-access-mgt/models/create-user-request.model';
import {
  GroupTypeToOrgUnitPair,
  IHaveGrantAccessPermissions,
  IApiRequestGrantAccess,
  IGrantAccessRequestsViewForm,
  removeThirdPartyFromGroupName,
} from '@carm/features/private/setup-my-portal/employees-access-mgt/models/grant-access.model';
import { AccessGroup, LoaderSpinnerTypes } from '@carm/_core';
import { HttpParams } from '@angular/common/http';
import { GrantAccessRequestsStateService } from '@carm/features/private/setup-my-portal/employees-access-mgt/services/grant-access-requests-state.service';

@Injectable()
export class StaffAccessRequestsService extends BaseApiService {
  constructor(protected injector: Injector,
    private grantAccesStateService: GrantAccessRequestsStateService) {
    super(injector);
  }
  get accessRequestsUrl(): string {
    return `${environment.baseApiUrls}/accessrequests/staffmember`;
  }

  get accessRequestsUrlB(): string {
    return `${this.endPointB}/accessrequests/staffmember`;
  }

  get accessRequestsUrlNB(): string {
    return `${this.endPointNB}/accessrequests/staffmember`;
  }

  public getAccessRequests(
    loaderSpinnerType: LoaderSpinnerTypes,
    sort: string,
    currentPage: number,
    pageSize: number,
    filterField: string = "",
    filterValue: string = ""): Observable<AccessRequestPendingResponse> {
    let params = new HttpParams()
      .set('currentPage', currentPage.toString())
      .set('pageSize', pageSize.toString())
      .set('sort', sort);
    if (filterField && filterValue) {
      params = params
        .set('filterField', filterField)
        .set('filterValue', filterValue);
    }
    const endpoint = `${this.accessRequestsUrlNB}/pending`;
    return this.httpAuthGet<AccessRequestPendingResponse>(endpoint, params, loaderSpinnerType);
  }

  public getAccessRequestDetails(uid: string): Observable<AccessRequestUid> {
    const endpoint = `${this.accessRequestsUrlNB}/accessrequest/${uid}`;
    return this.httpAuthGet<AccessRequestUid>(endpoint);
  }

  // Sends post request to API to mark the received pending request as rejected
  public rejectAccessRequest(
    uid: string,
    rejectionComments: string
  ): Observable<IBaseResponse> {
    const url = `${this.accessRequestsUrlNB}/reject`;
    return this.httpAuthPost<IBaseResponse>(url, {
      accessRequestUid: uid,
      rejectedComments: rejectionComments,
    });
  }

  public getUserRequest(): Observable<any> {
    const url = `${this.accessRequestsUrl}/myrequest`;
    return this.httpAuthGet<any>(url);
  }

  public find(businessId: string): Observable<FindBusinessResponse> {
    const params = new HttpParams().set('businessId', businessId);

    const url = `${environment.baseApiUrls}/accessrequests/staffmember/find`;
    return this.httpAuthGet<FindBusinessResponse>(url, params);
  }

  public createUserRequest(
    request: ICreateUserRequest
  ): Observable<IBaseResponse> {
    const url = `${this.accessRequestsUrl}/accessrequest/create`;

    const req = this.mapCreateUserRequest(request);
    return this.httpAuthPost<IBaseResponse>(url, req);
  }

  public cancelUserRequest(requestId: string): Observable<any> {
    const url = `${this.accessRequestsUrl}/accessrequest/${requestId}/cancel`;
    return this.httpAuthDelete<any>(url);
  }

  public getGrantAccessPermissionsForAccessRequests(): Observable<IHaveGrantAccessPermissions> {
    const userRequests = this.grantAccesStateService.getUserRequests();
    const formData = this.grantAccesStateService.grantAccessViewForm;
    const nativeCompanyUid = this.userService.getUserNativeRolesSync()[0].program.companyUid;
    const apiobj = this.mapApproveRequestFormToApi(
      userRequests.map(req => req.UserId),
      formData
    );
    const url =
      environment.business.accountManagement.baseUrl +
      '/business/' +
      nativeCompanyUid +
      '/employees/permission';
    return this.httpAuthPost<IHaveGrantAccessPermissions>(url,
      {
        staffMemberUids: apiobj.accessRequestUids,
        groupTypeToOrgUnitPairs: apiobj.groupTypeToOrgUnitPairs,
        sourceUid: nativeCompanyUid
      });
  }

  public approveRequests(): Observable<boolean> {
    const canApproveRequests = this.grantAccesStateService.canApproveRequests();
    const userRequests = this.grantAccesStateService.getUserRequests();
    const formData = this.grantAccesStateService.grantAccessViewForm;
    if (canApproveRequests) {
      const apiobj = this.mapApproveRequestFormToApi(
        userRequests.map(req => req.RequestId),
        formData
      );
      const url = this.accessRequestsUrlNB + '/approve';
      return this.httpAuthPost<IBaseResponse>(url, apiobj).pipe(
        map(r => r != null)
      );
    } else {
      return of(false);
    }
  }

  private mapApproveRequestFormToApi(
    entityIds: string[],
    formData: IGrantAccessRequestsViewForm
  ) {
    let apiObj: IApiRequestGrantAccess;
    const roleBamSelected = formData.programsAccessGroups.every(pg => pg.accessGroup === AccessGroup.BusinessAccountManager);
    if (roleBamSelected) {
      apiObj = {
        accessRequestUids: entityIds,
        groupTypeToOrgUnitPairs: {
          pairs: [
            {
              groupTypeUid: AccessGroup.BusinessAccountManager,
              orgUnitUid: this.userService.getUserRolesSync().business.uid,
            },
          ],
        },
      };
    } else {
      apiObj = {
        accessRequestUids: entityIds,
        groupTypeToOrgUnitPairs: {
          pairs: formData.programsAccessGroups
            .filter(g => g.accessGroup !== AccessGroup.NoAccess) // remove the NO_ACCESS groups
            .map(
              ag =>
                ({
                  groupTypeUid: ag.accessGroup,
                  orgUnitUid: ag.programInfo.uid,
                } as GroupTypeToOrgUnitPair)
            ),
        },
      };
    }
    return {
      ...apiObj,
      groupTypeToOrgUnitPairs: removeThirdPartyFromGroupName(apiObj?.groupTypeToOrgUnitPairs)
    };
  }

  mapCreateUserRequest(
    newRequest: ICreateUserRequest = null
  ): PostStaffMemberAccessRequest {
    const userRequest: PostStaffMemberAccessRequest = {
      requestComments: newRequest.userComments,
      verificationPin: newRequest.verificationPin,
      serviceProviderUid: newRequest.BN,
    };

    return userRequest;
  }
}
